import { apiService } from "@/services";
import { useMutation, useQuery } from "@tanstack/react-query";
import queryClient from "@/query-client";
import { DEFAULT_STALE_TIME } from "@/constants";

type BrandId = number | null;

// Query-key factory (conventions/03). Every Brand Intelligence key is brand-scoped (C-16) except config.
// The category-choice save writes `categoryChoice` and invalidates `keywordSov` through this one
// factory, so the Keyword SOV query and the invalidation cannot drift apart.
export const brandIntelligenceKeys = {
  all: ["brand-intelligence"] as const,
  config: () => [...brandIntelligenceKeys.all, "config"] as const,
  overview: (brandId: BrandId) => [...brandIntelligenceKeys.all, "overview", brandId] as const,
  build: (brandId: BrandId) => [...brandIntelligenceKeys.all, "build", brandId] as const,
  match: (brandId: BrandId, name: string) => [...brandIntelligenceKeys.all, "match", brandId, name] as const,
  aiVisibility: (brandId: BrandId) => [...brandIntelligenceKeys.all, "ai-visibility", brandId] as const,
  keywordSov: (brandId: BrandId) => [...brandIntelligenceKeys.all, "keyword-sov", brandId] as const,
  keywordContribution: (brandId: BrandId, keyword: string | null) =>
    [...brandIntelligenceKeys.all, "keyword-contribution", brandId, keyword] as const,
  competitors: (brandId: BrandId) => [...brandIntelligenceKeys.all, "competitors", brandId] as const,
  competitorDrawer: (brandId: BrandId, leafCategoryId: string | null) =>
    [...brandIntelligenceKeys.all, "competitor-drawer", brandId, leafCategoryId] as const,
  keywordHistory: (brandId: BrandId, keyword: string | null) =>
    [...brandIntelligenceKeys.all, "keyword-history", brandId, keyword] as const,
  // Without a leaf it is the brand-wide prefix, for invalidating every category's products at once.
  categoryProducts: (brandId: BrandId, leafCategoryId?: string | null) =>
    leafCategoryId === undefined
      ? ([...brandIntelligenceKeys.all, "category-products", brandId] as const)
      : ([...brandIntelligenceKeys.all, "category-products", brandId, leafCategoryId] as const),
  categorySov: (brandId: BrandId, leafCategoryId: string | null) =>
    [...brandIntelligenceKeys.all, "category-sov", brandId, leafCategoryId] as const,
  categoryChoice: (brandId: BrandId) => [...brandIntelligenceKeys.all, "category-choice", brandId] as const,
};

// AUT-245: Brand Intelligence read API. Response shapes mirror BrandIntelligenceController.
export interface BiHeaderStats {
  category_count: number;
  asin_count: number;
  trailing_12mo_revenue: number;
  // The Full-Catalog BSR threshold this report was built with (for the "N categories" tooltip).
  bsr_threshold: number;
}

export interface BiCategoryRow {
  leaf_category_id: string;
  name: string;
  path: string;
  brand_revenue: number;
  category_revenue: number;
  rev_share: number | null;
  brand_growth: number | null;
  category_growth: number | null;
  growth_status: string | null;
  organic_sov: number | null;
  sov_trend: "up" | "down" | "flat" | null;
  paid_sov: number | null;
  total_sov: number | null;
  hero_concentration: number | null;
  hero_alert: boolean;
  brand_visibility: number | null;
  brand_visibility_partial: boolean;
  health_status: string | null;
  brand_asins: number;
}

export interface BiOverview {
  brand: string;
  header: BiHeaderStats;
  built: boolean;
  categories: BiCategoryRow[];
}

export interface BiKeyword {
  keyword: string;
  monthly_volume: number;
}

export interface BiKeywordSov {
  keyword: string;
  brand: string;
  brand_weight: number;
  keyword_sov_pct: number;
}

export interface BiCategorySov {
  brand: string;
  leaf_category_id: string;
  category_sov_pct: number;
  keyword_set: BiKeyword[];
  keyword_sov: BiKeywordSov[];
}

export interface BiConfig {
  // Admin thresholds the UI shows in tooltips/labels/filters (so they aren't hardcoded).
  full_catalog_bsr_threshold: number;
  large_gap_pp: number;
}

// BI feature config: admin thresholds the UI reflects in tooltips/labels/filters.
export const useGetBrandIntelligenceConfig = () =>
  useQuery<BiConfig>({
    queryKey: brandIntelligenceKeys.config(),
    queryFn: () => apiService.get<BiConfig, undefined>(`/brand-intelligence/config`),
    staleTime: DEFAULT_STALE_TIME,
  });

export const useGetBrandIntelligenceOverview = (brandId: number | null) =>
  useQuery<BiOverview>({
    queryKey: brandIntelligenceKeys.overview(brandId),
    queryFn: () => apiService.get<BiOverview, undefined>(`/brand-intelligence/${brandId}/overview`),
    enabled: !!brandId,
    staleTime: DEFAULT_STALE_TIME,
  });

export type BiBuildStatus = "not_built" | "pending" | "building" | "complete" | "failed";

export interface BiBuildStage {
  key: string;
  label: string;
  status: "pending" | "building" | "complete" | "skipped";
  completed_at: string | null;
}

export interface BiBuild {
  status: BiBuildStatus;
  stages: BiBuildStage[];
  started_at?: string | null;
  completed_at?: string | null;
  refreshed_at: string | null;
  error?: string | null;
}

export const useGetBrandIntelligenceBuild = (brandId: number | null) =>
  useQuery<BiBuild>({
    queryKey: brandIntelligenceKeys.build(brandId),
    queryFn: () => apiService.get<BiBuild, undefined>(`/brand-intelligence/${brandId}/build`),
    enabled: !!brandId,
    // Poll while a build is in flight, otherwise idle.
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === "building" || s === "pending" ? 3000 : false;
    },
  });

// Section 6.2: save the manual AI competitor-set override (empty = back to auto-suggest).
export const useSaveAiCompetitorSet = () =>
  useMutation({
    mutationFn: ({ brandId, brands }: { brandId: number; brands: string[] }) =>
      apiService.post<{ status: string }, { brands: string[] }>(`/brand-intelligence/${brandId}/ai-competitor-set`, { brands }),
    onSuccess: (_d, { brandId }) => {
      queryClient.invalidateQueries({ queryKey: brandIntelligenceKeys.aiVisibility(brandId) });
    },
  });

export const useRefreshBrandIntelligence = () =>
  useMutation({
    mutationFn: ({ brandId, acknowledge }: { brandId: number; acknowledge?: boolean }) =>
      apiService.post<{ status: "building"; brand_id: number }, { acknowledge?: boolean }>(
        `/brand-intelligence/${brandId}/refresh`,
        { acknowledge },
      ),
    onSuccess: (_data, { brandId }) => {
      queryClient.invalidateQueries({ queryKey: brandIntelligenceKeys.build(brandId) });
    },
  });

// Section 11: add-a-brand match preview.
export interface BiMatchProduct {
  asin: string;
  title: string;
  price: number | null;
  revenue: number | null;
  image_url: string;
  leaf_category_id: string;
}

export interface BiMatchCategory {
  leaf_category_id: string;
  name: string;
  brand_revenue: number;
  brand_asins: number;
  default_checked: boolean;
}

export interface BiBrandMatch {
  matched: boolean;
  query: string;
  brand?: string;
  products?: BiMatchProduct[];
  categories?: BiMatchCategory[];
}

// Same preview as useMatchBrand, as a query so the confirmation step keys on the brand and the typed name.
export const useGetBrandIntelligenceMatch = (brandId: number | null, name: string, enabled: boolean) =>
  useQuery<BiBrandMatch>({
    queryKey: brandIntelligenceKeys.match(brandId, name),
    queryFn: () => apiService.post<BiBrandMatch, { name: string }>(`/brand-intelligence/match`, { name }),
    enabled: enabled && !!brandId,
    staleTime: DEFAULT_STALE_TIME,
    // No retry: each attempt bills about 13 s of warehouse time.
    retry: false,
    // Own property, so it overrides the default keepPreviousData: a switched brand never shows the previous one's match.
    placeholderData: undefined,
  });

export const useMatchBrand = () =>
  useMutation<BiBrandMatch, unknown, { name: string }>({
    mutationFn: ({ name }) => apiService.post<BiBrandMatch, { name: string }>(`/brand-intelligence/match`, { name }),
  });

export interface BiCompetitorRow {
  leaf_category_id: string;
  name: string;
  competitors: number;
  brand_rank: number | null;
  brand_sov: number;
  top_competitor: string | null;
  top_competitor_sov: number | null;
  sov_gap: number | null;
}

export interface BiCompetitors {
  brand: string;
  built: boolean;
  kpis: {
    categories_tracked: number;
    categories_leading: number;
    large_gap_categories: number;
  };
  categories: BiCompetitorRow[];
}

export interface BiCompetitorDrawerRow {
  brand: string;
  sov_pct: number;
  segment: string;
  trend: "up" | "down" | "flat" | null;
  avg_price: number | null;
  asin_count: number;
  revenue_in_category: number;
}

export interface BiCompetitorDrawer {
  brand: string;
  leaf_category_id: string;
  materialized: boolean;
  competitors: BiCompetitorDrawerRow[];
}

export interface BiAiPlatform {
  platform: string;
  usable_samples: number;
  brand_mention_rate: number;
  competitor_avg_rate: number;
  brand_rank: number | null;
  tracked_brands: number;
}

export interface BiAiPrompt {
  query: string;
  type: string | null;
  platform: string;
  leaf_category_id: string;
  category: string;
  usable: boolean;
  unusable_reason: string | null;
  brand_appeared: boolean;
  competitors_cited: string[];
  sentiment: string | null;
}

export interface BiAiGap {
  query: string;
  cause: string | null;
  action: string | null;
  reasoning: string | null;
  leaf_category_id: string;
  category: string;
}

export type BiAiTrendPoint = { month: string } & Record<string, number | string | null>;

export interface BiAiTrend {
  platforms: string[];
  brands: string[];
  mention: Record<string, BiAiTrendPoint[]>; // keyed by platform, incl. "all"
  // AUT-480: keyed by platform too, incl. "all" — it was a flat array, which is why the
  // "AI share of voice trend" chart had no platform switcher. Months snapshotted before that
  // change carry a value on "all" only.
  sov: Record<string, BiAiTrendPoint[]>;
}

export interface BiAiVisibility {
  brand: string;
  built: boolean;
  mention_share?: number;
  brand_mention_rate?: number;
  usable_samples?: number;
  total_samples?: number;
  competitor_set?: { brand: string; mention_rate: number }[];
  competitor_candidates?: string[];
  competitor_set_overridden?: boolean;
  platforms: BiAiPlatform[];
  prompts: BiAiPrompt[];
  sentiment?: { positive: number; neutral: number; negative: number };
  sentiment_samples?: { query: string; sentiment: string; excerpt: string }[];
  gaps?: BiAiGap[];
  trend?: BiAiTrend;
}

export const useGetBrandIntelligenceAiVisibility = (brandId: number | null) =>
  useQuery<BiAiVisibility>({
    queryKey: brandIntelligenceKeys.aiVisibility(brandId),
    queryFn: () => apiService.get<BiAiVisibility, undefined>(`/brand-intelligence/${brandId}/ai-visibility`),
    enabled: !!brandId,
    staleTime: DEFAULT_STALE_TIME,
  });

export interface BiKeywordBrandSov {
  brand: string;
  sov_pct: number;
}

export interface BiKeywordSovRow {
  keyword: string;
  monthly_volume: number;
  kw_revenue: number | null;
  leaf_category_ids: string[];
  brand_breakdown: BiKeywordBrandSov[];
}

export interface BiKeywordSovSummary {
  keyword_count: number;
  ranked_count: number;
  volume_weighted_sov: number | null;
}

export interface BiKeywordSovCategory extends BiKeywordSovSummary {
  leaf_category_id: string;
  name: string;
  brand_revenue: number;
}

// The brand's saved category choice, rolled up over just those categories; null when none is saved.
export interface BiKeywordSovChoice extends BiKeywordSovSummary {
  leaf_category_ids: string[];
}

export interface BiKeywordSovMatrix {
  brand: string;
  built: boolean;
  brands: string[];
  categories: BiKeywordSovCategory[];
  all: BiKeywordSovSummary | null;
  choice: BiKeywordSovChoice | null;
  keywords: BiKeywordSovRow[];
}

export const useGetBrandIntelligenceKeywordSov = (brandId: number | null) =>
  useQuery<BiKeywordSovMatrix>({
    queryKey: brandIntelligenceKeys.keywordSov(brandId),
    queryFn: () => apiService.get<BiKeywordSovMatrix, undefined>(`/brand-intelligence/${brandId}/keyword-sov`),
    enabled: !!brandId,
    staleTime: DEFAULT_STALE_TIME,
  });

export interface BiKeywordContributionProduct {
  asin: string;
  title: string;
  contribution_pct: number;
}

export interface BiKeywordContribution {
  keyword: string;
  products: BiKeywordContributionProduct[];
}

export const useGetBrandIntelligenceKeywordContribution = (brandId: number | null, keyword: string | null) =>
  useQuery<BiKeywordContribution>({
    queryKey: brandIntelligenceKeys.keywordContribution(brandId, keyword),
    queryFn: () =>
      apiService.get<BiKeywordContribution, { keyword: string }>(`/brand-intelligence/${brandId}/keyword-contribution`, {
        keyword: keyword ?? "",
      }),
    enabled: !!brandId && !!keyword,
    staleTime: DEFAULT_STALE_TIME,
  });

export const useGetBrandIntelligenceCompetitors = (brandId: number | null) =>
  useQuery<BiCompetitors>({
    queryKey: brandIntelligenceKeys.competitors(brandId),
    queryFn: () => apiService.get<BiCompetitors, undefined>(`/brand-intelligence/${brandId}/competitors`),
    enabled: !!brandId,
    staleTime: DEFAULT_STALE_TIME,
  });

export const useGetBrandIntelligenceCompetitorDrawer = (brandId: number | null, leafCategoryId: string | null) =>
  useQuery<BiCompetitorDrawer>({
    queryKey: brandIntelligenceKeys.competitorDrawer(brandId, leafCategoryId),
    queryFn: () =>
      apiService.get<BiCompetitorDrawer, undefined>(
        `/brand-intelligence/${brandId}/competitors/${encodeURIComponent(leafCategoryId ?? "")}/drawer`,
      ),
    enabled: !!brandId && !!leafCategoryId,
    staleTime: DEFAULT_STALE_TIME,
  });

export interface BiCategoryProduct {
  asin: string;
  title: string;
  price: number | null;
  revenue: number;
  image_url: string;
  added: boolean;
  content_scorecard: number | null;
  product_sov: number | null;
  product_sov_estimated: boolean;
  // AUT-313: live Signal attributes, populated only for added products.
  search_volume: number | null;
  keywords_pg1_count: number | null;
  ab_tests_running: number | null;
  themes_count: number | null;
}

export interface BiKeywordHistoryPoint {
  month: string;
  product_sov: number | null;
}

// §5.3/§5.4: one row per contributing product, each with its 12-month SOV points aligned to the
// shared `months` axis. The latest month is the current contribution; per month the products'
// SOV sums to the brand's Keyword SOV.
export interface BiKeywordHistoryProduct {
  asin: string;
  title: string;
  sub_category: string;
  image_url: string;
  current_sov: number;
  points: BiKeywordHistoryPoint[];
}

export interface BiKeywordHistory {
  keyword: string;
  months: string[];
  // false: the brand predates the per-category keyword sets; its history arrives with the next Refresh.
  materialized: boolean;
  products: BiKeywordHistoryProduct[];
}

export const useGetBrandIntelligenceKeywordHistory = (brandId: number | null, keyword: string | null) =>
  useQuery<BiKeywordHistory>({
    queryKey: brandIntelligenceKeys.keywordHistory(brandId, keyword),
    queryFn: () =>
      apiService.get<BiKeywordHistory, { keyword: string }>(`/brand-intelligence/${brandId}/keyword-history`, {
        keyword: keyword ?? "",
      }),
    enabled: !!brandId && !!keyword,
    staleTime: DEFAULT_STALE_TIME,
  });

export interface BiCategoryProducts {
  leaf_category_id: string;
  products: BiCategoryProduct[];
}

export const useGetBrandIntelligenceCategoryProducts = (brandId: number | null, leafCategoryId: string | null) =>
  useQuery<BiCategoryProducts>({
    queryKey: brandIntelligenceKeys.categoryProducts(brandId, leafCategoryId),
    queryFn: () =>
      apiService.get<BiCategoryProducts, undefined>(
        `/brand-intelligence/${brandId}/categories/${encodeURIComponent(leafCategoryId ?? "")}/products`,
      ),
    enabled: !!brandId && !!leafCategoryId,
    staleTime: DEFAULT_STALE_TIME,
  });

export const useGetBrandIntelligenceCategorySov = (brandId: number | null, leafCategoryId: string | null) =>
  useQuery<BiCategorySov>({
    queryKey: brandIntelligenceKeys.categorySov(brandId, leafCategoryId),
    queryFn: () =>
      apiService.get<BiCategorySov, undefined>(
        `/brand-intelligence/${brandId}/categories/${encodeURIComponent(leafCategoryId ?? "")}/sov`,
      ),
    enabled: !!brandId && !!leafCategoryId,
    staleTime: DEFAULT_STALE_TIME,
  });

// The categories the brand's report reads, and the ones the user chose to read it by.
export interface BiCategoryChoice {
  has_report: boolean;
  categories: { leaf_category_id: string; name: string }[];
}

export const useGetBrandIntelligenceCategoryChoice = (brandId: number | null) =>
  useQuery<BiCategoryChoice>({
    queryKey: brandIntelligenceKeys.categoryChoice(brandId),
    queryFn: () => apiService.get<BiCategoryChoice, undefined>(`/brand-intelligence/${brandId}/category-choice`),
    enabled: !!brandId,
    staleTime: DEFAULT_STALE_TIME,
    // Own property, so it overrides the default keepPreviousData: a switched brand never shows the previous one's choice.
    placeholderData: undefined,
  });

export const useSaveBrandIntelligenceCategoryChoice = () =>
  useMutation({
    mutationFn: ({ brandId, leafCategoryIds }: { brandId: number; leafCategoryIds: string[] }) =>
      apiService.put<BiCategoryChoice, { leaf_category_ids: string[] }>(`/brand-intelligence/${brandId}/category-choice`, {
        leaf_category_ids: leafCategoryIds,
      }),
    onSuccess: (data, { brandId }) => {
      queryClient.setQueryData(brandIntelligenceKeys.categoryChoice(brandId), data);
      // void, and nothing returned: TanStack awaits a promise returned here before the mutation settles.
      void queryClient.invalidateQueries({ queryKey: brandIntelligenceKeys.keywordSov(brandId) });
    },
  });
